// Live Settings — fully wired: editable display name + notification prefs
// (PATCH /auth/me), password change, TOTP 2FA enroll/confirm/disable, and the
// appearance toggle.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { AppearanceToggle, KickerRow, PageHeader, Pill } from '../../components/shell';
import { CredentialRow, type CredentialAccount } from '../../components/CredentialRow';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

const NOTIF_KEYS: { key: string; label: string }[] = [
  { key: 'statement_available', label: 'Statement available' },
  { key: 'reconciliation_alert', label: 'Reconciliation alerts' },
  { key: 'contact_updates', label: 'Replies to my requests' },
];

export default function Settings() {
  const { user, signOut } = useAuth();
  if (!user) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
                    textTransform: 'uppercase', color: 'var(--ink-3)' }}>Loading…</p>
      </div>
    );
  }

  return (
    <div className="view container">
      <PageHeader kicker="ACCOUNT" title={<><em>Settings.</em></>} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 56, maxWidth: 760 }}>
        <ProfileSection user={user} />
        <NotificationsSection user={user} />
        <SecuritySection user={user} />
        <ConnectionsSection />
        <ApiKeysSection user={user} />
        <CompanyInvitesSection user={user} />
        <Section kicker="APPEARANCE">
          <SettingsRow label="VIEW"
            value={<div>
              <span className="serif-body" style={{ fontSize: 15, fontStyle: 'italic', display: 'block', marginBottom: 12 }}>
                Clean is a compact, single-screen accounting view. Editorial is the classic layout.
              </span>
              <AppearanceToggle />
            </div>} />
        </Section>
        <Section kicker="SESSION">
          <SettingsRow label="SIGN OUT"
            value={<span className="serif-body" style={{ fontSize: 15, fontStyle: 'italic' }}>Ends this browser session.</span>}
            action={<button className="btn-link" onClick={() => { void signOut(); }}>Sign out</button>} />
        </Section>
      </div>
    </div>
  );
}

function ProfileSection({ user }: { user: any }) {
  const [name, setName] = useState(user.display_name || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const save = async () => {
    setSaving(true); setMsg(null);
    try { await api('/auth/me', { method: 'PATCH', body: { display_name: name } }); setMsg('Saved.'); }
    catch (e: any) { setMsg(e?.payload?.detail || 'Save failed.'); }
    finally { setSaving(false); }
  };
  return (
    <Section kicker="PROFILE">
      <SettingsRow label="DISPLAY NAME"
        value={<input className="field-input" value={name} onChange={e => setName(e.target.value)}
                      style={{ maxWidth: 360 }} placeholder="Your name" />}
        action={<button className="btn-link" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>} />
      {msg && <Note>{msg}</Note>}
      <SettingsRow label="EMAIL" value={<span className="num" style={{ fontSize: 14 }}>{user.email}</span>}
        action={<span className="label-s" style={{ color: 'var(--ink-3)' }}>Contact desk to change</span>} />
      <SettingsRow label="LEGAL ENTITY" value={<span className="serif" style={{ fontSize: 18 }}>{user.client_legal_name || '—'}</span>} />
      <SettingsRow label="ROLE" value={<span className="serif" style={{ fontSize: 16, textTransform: 'capitalize' }}>{user.role}</span>} />
    </Section>
  );
}

function NotificationsSection({ user }: { user: any }) {
  const [prefs, setPrefs] = useState<Record<string, boolean>>(user.notification_preferences || {});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const toggle = async (key: string) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); setSaving(true); setMsg(null);
    try { await api('/auth/me', { method: 'PATCH', body: { notification_preferences: next } }); setMsg('Saved.'); }
    catch (e: any) { setMsg(e?.payload?.detail || 'Save failed.'); setPrefs(prefs); }
    finally { setSaving(false); }
  };
  return (
    <Section kicker="NOTIFICATIONS">
      {NOTIF_KEYS.map(n => (
        <SettingsRow key={n.key} label={n.label.toUpperCase()}
          value={<span className="serif-body" style={{ fontSize: 15, fontStyle: 'italic', color: 'var(--ink-2)' }}>
            {prefs[n.key] ? 'On' : 'Off'}</span>}
          action={<button className="btn-link" disabled={saving} onClick={() => toggle(n.key)}>
            {prefs[n.key] ? 'Turn off' : 'Turn on'}</button>} />
      ))}
      {msg && <Note>{msg}</Note>}
    </Section>
  );
}

function SecuritySection({ user }: { user: any }) {
  // Password change.
  const [showPw, setShowPw] = useState(false);
  const [cur, setCur] = useState(''); const [nw, setNw] = useState('');
  const [pwMsg, setPwMsg] = useState<string | null>(null); const [pwBusy, setPwBusy] = useState(false);
  const changePw = async () => {
    setPwBusy(true); setPwMsg(null);
    try {
      await api('/auth/password/change', { method: 'POST', body: { current_password: cur, new_password: nw } });
      setPwMsg('Password changed.'); setCur(''); setNw(''); setShowPw(false);
    } catch (e: any) { setPwMsg(e?.payload?.detail || 'Change failed.'); }
    finally { setPwBusy(false); }
  };

  // 2FA. Staff (manager/auditor/super-user) get a soft "recommended" nudge
  // when not enrolled; 2FA stays optional for everyone per operator decision.
  const isStaff = user.role === 'manager' || user.role === 'auditor' || !!user.is_superuser;
  const [enabled, setEnabled] = useState<boolean>(!!user.two_factor_enabled);
  const [enroll, setEnroll] = useState<{ qr_data_url: string } | null>(null);
  const [code, setCode] = useState(''); const [twoMsg, setTwoMsg] = useState<string | null>(null);
  const [disPw, setDisPw] = useState(''); const [disTotp, setDisTotp] = useState(''); const [disabling, setDisabling] = useState(false);
  const beginEnroll = async () => {
    setTwoMsg(null);
    try { setEnroll(await api('/auth/2fa/enroll', { method: 'POST', body: {} })); }
    catch (e: any) { setTwoMsg(e?.payload?.detail || 'Could not start enrollment.'); }
  };
  const confirm = async () => {
    setTwoMsg(null);
    try { await api('/auth/2fa/confirm', { method: 'POST', body: { code: code.trim() } }); setEnabled(true); setEnroll(null); setCode(''); setTwoMsg('Two-factor enrolled.'); }
    catch (e: any) { setTwoMsg(e?.payload?.detail || 'Invalid code.'); }
  };
  const disable = async () => {
    setDisabling(true); setTwoMsg(null);
    try {
      await api('/auth/2fa/disable', {
        method: 'POST',
        body: { current_password: disPw, totp_code: disTotp.trim() },
      });
      setEnabled(false); setDisPw(''); setDisTotp(''); setTwoMsg('Two-factor disabled.');
    }
    catch (e: any) { setTwoMsg(e?.payload?.detail || 'Could not disable.'); }
    finally { setDisabling(false); }
  };

  return (
    <Section kicker="SECURITY">
      <SettingsRow label="PASSWORD"
        value={<span className="serif-body" style={{ fontSize: 15, fontStyle: 'italic' }}>Change your sign-in password.</span>}
        action={<button className="btn-link" onClick={() => setShowPw(v => !v)}>{showPw ? 'Cancel' : 'Change'}</button>} />
      {showPw && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0 16px', maxWidth: 360 }}>
          <input className="field-input" type="password" placeholder="Current password" value={cur} onChange={e => setCur(e.target.value)} autoComplete="current-password" />
          <input className="field-input" type="password" placeholder="New password" value={nw} onChange={e => setNw(e.target.value)} autoComplete="new-password" />
          <button className="btn" disabled={pwBusy || !cur || !nw} onClick={changePw}>{pwBusy ? 'Saving…' : 'Update password'}</button>
        </div>
      )}
      {pwMsg && <Note>{pwMsg}</Note>}

      <SettingsRow label="TWO-FACTOR"
        value={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
            <Pill tone={enabled ? 'accent' : 'warn'}>{enabled ? 'ENABLED' : 'NOT ENROLLED'}</Pill>
            {!enabled && isStaff && <Pill>RECOMMENDED FOR STAFF ACCOUNTS</Pill>}
          </div>
        }
        action={enabled
          ? null
          : (enroll ? null : <button className="btn-link" onClick={beginEnroll}>Enroll</button>)} />
      {enroll && (
        <div style={{ display: 'flex', gap: 24, padding: '8px 0 16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <img src={enroll.qr_data_url} alt="2FA QR code" width={148} height={148} style={{ border: 'var(--hairline)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 280 }}>
            <span className="serif-body" style={{ fontSize: 14, color: 'var(--ink-2)' }}>
              Scan with an authenticator app, then enter the 6-digit code to confirm.
            </span>
            <input className="field-input num" inputMode="numeric" placeholder="123456" value={code} onChange={e => setCode(e.target.value)} />
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn" onClick={confirm} disabled={!code}>Confirm</button>
              <button className="btn-link" onClick={() => { setEnroll(null); setCode(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {enabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0 16px', maxWidth: 360 }}>
          <input className="field-input" type="password" placeholder="Password" value={disPw} onChange={e => setDisPw(e.target.value)} autoComplete="current-password" />
          <input className="field-input num" inputMode="numeric" placeholder="6-digit code from your authenticator" value={disTotp} onChange={e => setDisTotp(e.target.value)} />
          <button className="btn-ghost btn" disabled={disabling || !disPw || !disTotp} onClick={disable} style={{ alignSelf: 'flex-start' }}>{disabling ? 'Disabling…' : 'Disable 2FA'}</button>
        </div>
      )}
      {twoMsg && <Note>{twoMsg}</Note>}
    </Section>
  );
}

function ConnectionsSection() {
  const [accounts, setAccounts] = useState<CredentialAccount[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<{ accounts: CredentialAccount[] }>('/portfolio/holdings/')
      .then(d => {
        if (cancelled) return;
        setAccounts(d.accounts.filter(a => a.venue === 'coinbase_exchange'));
      })
      .catch((e: any) => {
        if (cancelled) return;
        // 409 = portfolio not yet provisioned; treat as "no connectable accounts".
        if (e?.status === 409) { setAccounts([]); return; }
        setLoadError('Failed to load your accounts.');
      });
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Nothing to render for clients whose accounts are all on-chain or Bybit.
  if (accounts !== null && accounts.length === 0 && !loadError) return null;

  return (
    <Section kicker="EXCHANGE CONNECTIONS">
      {loadError && (
        <SettingsRow label="STATUS"
          value={<span className="serif-body" style={{ color: 'var(--accent)' }}>{loadError}</span>} />
      )}
      {accounts === null && !loadError && (
        <SettingsRow label="LOADING"
          value={<span className="label-s" style={{ color: 'var(--ink-3)' }}>…</span>} />
      )}
      {accounts?.map(a => (
        <CredentialRow key={a.id} account={a} onChange={() => setReloadKey(k => k + 1)} />
      ))}
    </Section>
  );
}

// APIKEY.5 — manager programmatic API keys. Read-only key for the /api/v1/ext
// external API; created here (cookie+2FA session), shown once, revocable.
interface ApiKeyRow {
  id: string;
  name: string;
  token_key: string;
  status: 'active' | 'expired' | 'revoked';
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
}
interface ApiKeyCreated extends ApiKeyRow { api_key: string }

const fmtKeyDate = (s: string | null): string => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 10);
};

function ApiKeysSection({ user }: { user: any }) {
  const [rows, setRows] = useState<ApiKeyRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // The raw secret is held in memory only, shown once, cleared on dismiss.
  const [freshKey, setFreshKey] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyErr, setCopyErr] = useState(false);
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Manager surface. Mirror the backend gate (is_superuser OR role==manager),
  // matching lib/auth RequireManagerOrSuperUser + the /ops authz — otherwise a
  // super-user allowed by the endpoint can't see or manage keys in the UI.
  const isManager = user.is_superuser || user.role === 'manager';

  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    api<ApiKeyRow[]>('/ops/api-keys')
      .then(d => { if (!cancelled) { setRows(d); setLoadError(null); } })
      .catch(() => { if (!cancelled) setLoadError('Failed to load your API keys.'); });
    return () => { cancelled = true; };
  }, [isManager, reloadKey]);

  if (!isManager) return null;

  async function create(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setFormErr(null);
    const trimmed = name.trim();
    if (!trimmed) { setFormErr('Enter a name for the key.'); return; }
    setBusy(true);
    try {
      const created = await api<ApiKeyCreated>('/ops/api-keys', {
        method: 'POST',
        body: { name: trimmed, expires_at: expiresAt || undefined },
      });
      setFreshKey(created);
      setCopied(false);
      setName('');
      setExpiresAt('');
      setReloadKey(k => k + 1);
    } catch (err: any) {
      setFormErr(err?.payload?.detail || 'Could not create the key.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string, keyName: string) {
    if (revokingId) return;
    if (!window.confirm(`Revoke the API key "${keyName}"? Any integration using it will stop working.`)) return;
    setActionErr(null);
    setRevokingId(id);
    try {
      await api(`/ops/api-keys/${id}/revoke`, { method: 'POST' });
      setReloadKey(k => k + 1);
    } catch {
      setActionErr('Could not revoke the key.');
    } finally {
      setRevokingId(null);
    }
  }

  async function copyKey() {
    if (!freshKey) return;
    setCopyErr(false);
    try {
      if (!navigator.clipboard) throw new Error('no clipboard');
      await navigator.clipboard.writeText(freshKey.api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      setCopyErr(true);
    }
  }

  return (
    <Section kicker="API KEYS">
      <div style={{ padding: '18px 0', borderBottom: 'var(--hairline)' }}>
        <span className="serif-body" style={{ fontSize: 15, fontStyle: 'italic', display: 'block', marginBottom: 4 }}>
          A read-only key lets an integration fetch your fund data through the API without an interactive login.
        </span>
        <span className="serif-body" style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          Keys are read-only and can be revoked at any time. Creating or revoking a key needs your full sign-in.
        </span>
      </div>

      {/* Show-once secret panel — the ONLY time the raw key is visible. */}
      {freshKey && (
        <div style={{ padding: '18px 0', borderBottom: 'var(--hairline)' }}>
          <span className="label-s" style={{ display: 'block', marginBottom: 8 }}>NEW KEY — SHOWN ONCE</span>
          <div className="num" style={{ fontSize: 14, wordBreak: 'break-all', padding: '10px 12px',
                                        background: 'var(--tint)', border: 'var(--hairline)' }}>
            {freshKey.api_key}
          </div>
          <p className="serif-body" style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--oxblood)', fontStyle: 'italic' }}>
            Copy this now. For your security it is not stored and cannot be shown again.
          </p>
          <div style={{ marginTop: 12, display: 'flex', gap: 20, alignItems: 'center' }}>
            <button type="button" className="btn-link" onClick={() => { void copyKey(); }}>
              {copied ? 'Copied' : 'Copy key'}
            </button>
            <button type="button" className="btn-link" onClick={() => { setFreshKey(null); setCopyErr(false); }}>Done</button>
          </div>
          {copyErr && (
            <p className="serif-body" style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--ink-3)', fontStyle: 'italic' }}>
              Copy is unavailable here. Select the key above and copy it manually.
            </p>
          )}
        </div>
      )}

      {/* Create form */}
      <form onSubmit={create} style={{ padding: '18px 0', borderBottom: 'var(--hairline)' }}>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: '1 1 240px' }}>
            <label className="field-label" htmlFor="apikey-name">NAME</label>
            <input id="apikey-name" className="field-input" type="text" maxLength={120}
                   placeholder="e.g. reporting bot" value={name}
                   onChange={e => setName(e.target.value)} />
          </div>
          <div className="field" style={{ flex: '0 0 200px' }}>
            <label className="field-label" htmlFor="apikey-expiry">EXPIRES (OPTIONAL)</label>
            <input id="apikey-expiry" className="field-input" type="date" value={expiresAt}
                   onChange={e => setExpiresAt(e.target.value)} />
          </div>
          <button className="btn" type="submit" disabled={busy}>
            {busy ? 'Creating' : 'Create key'}<span className="btn-arrow">→</span>
          </button>
        </div>
        {formErr && (
          <p className="serif-body" style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--oxblood)', fontStyle: 'italic' }}>
            {formErr}
          </p>
        )}
      </form>

      {/* List */}
      {loadError && <Note>{loadError}</Note>}
      {actionErr && <Note>{actionErr}</Note>}
      {rows === null && !loadError && (
        <div style={{ padding: '18px 0' }}>
          <span className="label-s" style={{ color: 'var(--ink-3)' }}>LOADING…</span>
        </div>
      )}
      {rows !== null && rows.length === 0 && (
        <div style={{ padding: '18px 0' }}>
          <span className="serif-body" style={{ fontSize: 15, fontStyle: 'italic', color: 'var(--ink-3)' }}>
            No API keys yet.
          </span>
        </div>
      )}
      {rows !== null && rows.length > 0 && (
        <table className="tbl" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>NAME</th>
              <th>KEY</th>
              <th>STATUS</th>
              <th data-align="right">EXPIRES</th>
              <th data-align="right">LAST USED</th>
              <th data-align="right">CREATED</th>
              <th data-align="right"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ opacity: r.status === 'active' ? 1 : 0.5 }}>
                <td>{r.name}</td>
                <td className="num">{r.token_key}…</td>
                <td><Pill tone={r.status === 'active' ? 'accent' : 'warn'}>{r.status.toUpperCase()}</Pill></td>
                <td data-align="right" className="num">{fmtKeyDate(r.expires_at)}</td>
                <td data-align="right" className="num">{fmtKeyDate(r.last_used_at)}</td>
                <td data-align="right" className="num">{fmtKeyDate(r.created_at)}</td>
                <td data-align="right">
                  {r.status === 'active' && (
                    <button className="btn-link" disabled={revokingId === r.id}
                            onClick={() => { void revoke(r.id, r.name); }}>
                      {revokingId === r.id ? 'Revoking' : 'Revoke'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

// CMI.4 — manager frontend for co-manager invites. Invite a second manager into
// the SAME company by email; the invitee accepts at /join/{token} (CMI.5).
interface CompanyInviteRow {
  id: string;
  email: string;
  token: string;
  status: 'active' | 'expired' | 'revoked' | 'accepted';
  expires_at: string | null;
  accepted_at: string | null;
  created_at: string;
  is_usable: boolean;
}

const inviteStatusTone = (s: CompanyInviteRow['status']): '' | 'ink' | 'accent' | 'warn' =>
  s === 'active' ? 'accent' : s === 'accepted' ? 'ink' : 'warn';

function CompanyInvitesSection({ user }: { user: any }) {
  const [rows, setRows] = useState<CompanyInviteRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [email, setEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Mirror the backend gate (is_superuser OR role==manager) — the /ops
  // company-invite endpoints allow both.
  const isManager = user.is_superuser || user.role === 'manager';

  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    api<CompanyInviteRow[]>('/ops/company-invites')
      .then(d => { if (!cancelled) { setRows(d); setLoadError(null); } })
      .catch(() => { if (!cancelled) setLoadError('Failed to load your co-manager invites.'); });
    return () => { cancelled = true; };
  }, [isManager, reloadKey]);

  if (!isManager) return null;

  const inviteUrl = (token: string) => `${window.location.origin}/join/${token}`;

  async function create(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setFormErr(null);
    setNote(null);
    const trimmed = email.trim();
    if (!trimmed) { setFormErr('Enter an email to invite.'); return; }
    setBusy(true);
    try {
      await api<CompanyInviteRow>('/ops/company-invites', {
        method: 'POST',
        body: { email: trimmed, expires_at: expiresAt || undefined },
      });
      setNote('Invite created. Copy its link from the list below and send it to your co-manager.');
      setEmail('');
      setExpiresAt('');
      setReloadKey(k => k + 1);
    } catch (err: any) {
      setFormErr(err?.payload?.detail || 'Could not create the invite.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string, inviteEmail: string) {
    if (revokingId) return;
    if (!window.confirm(`Revoke the invite for ${inviteEmail}? The link will stop working.`)) return;
    setActionErr(null);
    setNote(null);
    setRevokingId(id);
    try {
      await api(`/ops/company-invites/${id}/revoke`, { method: 'POST' });
      setReloadKey(k => k + 1);
    } catch {
      setActionErr('Could not revoke the invite.');
    } finally {
      setRevokingId(null);
    }
  }

  async function copyLink(row: CompanyInviteRow) {
    setNote(null);
    try {
      if (!navigator.clipboard) throw new Error('no clipboard');
      await navigator.clipboard.writeText(inviteUrl(row.token));
      setCopiedId(row.id);
      setTimeout(() => setCopiedId(c => (c === row.id ? null : c)), 2000);
    } catch {
      setActionErr('Copy is unavailable here. The invite link is ' + inviteUrl(row.token));
    }
  }

  return (
    <Section kicker="CO-MANAGERS">
      <div style={{ padding: '18px 0', borderBottom: 'var(--hairline)' }}>
        <span className="serif-body" style={{ fontSize: 15, fontStyle: 'italic', display: 'block', marginBottom: 4 }}>
          Invite a colleague to co-manage this company. They get the same manager access — but only within your company.
        </span>
        <span className="serif-body" style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          The invite goes to a brand-new email; the invitee sets a password, verifies their address, and enrolls two-factor on first sign-in.
        </span>
      </div>

      {/* Invite form */}
      <form onSubmit={create} style={{ padding: '18px 0', borderBottom: 'var(--hairline)' }}>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: '1 1 260px' }}>
            <label className="field-label" htmlFor="cmi-email">EMAIL</label>
            <input id="cmi-email" className="field-input" type="email" maxLength={254}
                   placeholder="colleague@yourfirm.com" value={email}
                   onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="field" style={{ flex: '0 0 200px' }}>
            <label className="field-label" htmlFor="cmi-expiry">EXPIRES (OPTIONAL)</label>
            <input id="cmi-expiry" className="field-input" type="date" value={expiresAt}
                   onChange={e => setExpiresAt(e.target.value)} />
          </div>
          <button className="btn" type="submit" disabled={busy}>
            {busy ? 'Sending' : 'Send invite'}<span className="btn-arrow">→</span>
          </button>
        </div>
        {note && (
          <p className="serif-body" style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--ink-2)' }}>{note}</p>
        )}
        {formErr && (
          <p className="serif-body" style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--oxblood)', fontStyle: 'italic' }}>
            {formErr}
          </p>
        )}
      </form>

      {/* List */}
      {loadError && <Note>{loadError}</Note>}
      {actionErr && <Note>{actionErr}</Note>}
      {rows === null && !loadError && (
        <div style={{ padding: '18px 0' }}>
          <span className="label-s" style={{ color: 'var(--ink-3)' }}>LOADING…</span>
        </div>
      )}
      {rows !== null && rows.length === 0 && (
        <div style={{ padding: '18px 0' }}>
          <span className="serif-body" style={{ fontSize: 15, fontStyle: 'italic', color: 'var(--ink-3)' }}>
            No co-manager invites yet.
          </span>
        </div>
      )}
      {rows !== null && rows.length > 0 && (
        <table className="tbl" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>EMAIL</th>
              <th>STATUS</th>
              <th data-align="right">EXPIRES</th>
              <th data-align="right">CREATED</th>
              <th data-align="right"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ opacity: r.status === 'active' ? 1 : 0.5 }}>
                <td>{r.email}</td>
                <td><Pill tone={inviteStatusTone(r.status)}>{r.status.toUpperCase()}</Pill></td>
                <td data-align="right" className="num">{fmtKeyDate(r.expires_at)}</td>
                <td data-align="right" className="num">{fmtKeyDate(r.created_at)}</td>
                <td data-align="right">
                  {(r.is_usable || r.status === 'active') && (
                    <span style={{ display: 'inline-flex', gap: 16 }}>
                      {r.is_usable && (
                        <button className="btn-link" onClick={() => { void copyLink(r); }}>
                          {copiedId === r.id ? 'Copied' : 'Copy link'}
                        </button>
                      )}
                      {r.status === 'active' && (
                        <button className="btn-link" disabled={revokingId === r.id}
                                onClick={() => { void revoke(r.id, r.email); }}>
                          {revokingId === r.id ? 'Revoking' : 'Revoke'}
                        </button>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function Section({ kicker, children }: { kicker: string; children: ReactNode }) {
  return (
    <section>
      <KickerRow kicker={kicker} />
      <div style={{ borderTop: '1px solid var(--ink)' }}>{children}</div>
    </section>
  );
}

function SettingsRow({ label, value, action }: { label: string; value: ReactNode; action?: ReactNode }) {
  return (
    <div style={{ padding: '18px 0', borderBottom: 'var(--hairline)',
                  display: 'grid', gridTemplateColumns: '200px 1fr auto', gap: 28, alignItems: 'center' }}>
      <span className="label-s">{label}</span>
      <div>{value}</div>
      <div>{action}</div>
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="serif-body" style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--accent)' }}>{children}</p>;
}
